<script setup>
import { computed } from 'vue'
import SearchResultRow from './search-result-row'

const props = defineProps({
    query: {
        type: String,
        default: '',
    },
    rows: {
        type: Array,
        default: () => [],
    },
    bestMatch: {
        type: Object,
        default: null,
    },
    recents: {
        type: Array,
        default: () => [],
    },
    hasSearched: {
        type: Boolean,
        default: false,
    },
    isLoading: {
        type: Boolean,
        default: false,
    },
    // Index into `options` (-1 = nothing selected); the field owns the keyboard.
    activeIndex: {
        type: Number,
        default: -1,
    },
    // ↑↓ Navigate · ↵ Search · Esc Close — desktop only.
    hintRow: {
        type: Boolean,
        default: false,
    },
    listboxId: {
        type: String,
        required: true,
    },
})

const emit = defineEmits(['activate', 'remove-recent'])

const isEmptyQuery = computed(() => !(props.query || '').trim())
const resultRows = computed(() => (props.bestMatch ? [props.bestMatch, ...props.rows] : props.rows))
const hasResults = computed(() => resultRows.value.length > 0)
// Not while the next keyword is pending: useTypeahead flips isLoading as soon as it
// arrives, and the empty rows still belong to the previous query.
const showNoMatch = computed(() => !isEmptyQuery.value && props.hasSearched && !props.isLoading && !hasResults.value)
const showSeeAll = computed(() => hasResults.value || showNoMatch.value)

// Everything the keyboard can land on, in DOM order.
const options = computed(() => {
    if (isEmptyQuery.value) return props.recents.map(title => ({ kind: 'recent', id: title, title }))
    // See all only once it renders (results, or the no-match state) — never an id with no element.
    return showSeeAll.value ? [...resultRows.value, { kind: 'see-all' }] : []
})

const optionId = index => `${props.listboxId}-option-${index}`
const optionIds = computed(() => options.value.map((_, index) => optionId(index)))
const seeAllIndex = computed(() => options.value.length - 1)
const isSeeAllSelected = computed(() => seeAllIndex.value === props.activeIndex)

defineExpose({ optionIds, options })
</script>

<template>
    <div
        class="search-typeahead"
        @pointerdown.prevent
    >
        <div
            v-if="hintRow && !isEmptyQuery"
            class="hint-row"
            aria-hidden="true"
        >
            <span><kbd class="key">↑</kbd><kbd class="key">↓</kbd> Navigate</span>
            <span><kbd class="key">↵</kbd> Search</span>
            <span><kbd class="key">Esc</kbd> Close</span>
        </div>

        <!-- role="listbox" may own only options and groups, so the heading and the no-match copy
             stay on the panel, outside it (same rule as _base-context-menu.vue's grabber and
             header). Both keep the panel's @pointerdown.prevent, which bubbles from here. -->
        <div
            v-if="isEmptyQuery"
            class="section-heading"
        >Recent searches</div>

        <!-- Unstyled: an empty block adds no box. No aria-live here — the whole panel is
             `v-if`'d away between requests, so a region mounted with this component would be
             born already holding its text, and that is not announced. SearchField owns the
             live region; this wrapper only keeps the copy out of role="listbox". -->
        <div class="live-region">
            <div
                v-if="showNoMatch"
                class="empty-message"
            >No quick matches for “{{ query.trim() }}”</div>
        </div>

        <!-- Unstyled on purpose: a plain block wrapper leaves the rows' layout unchanged. -->
        <div
            :id="listboxId"
            class="listbox"
            role="listbox"
            aria-label="Quick results"
        >
            <template v-if="isEmptyQuery">
                <SearchResultRow
                    v-for="(option, index) in options"
                    :id="optionId(index)"
                    :key="option.id"
                    :row="option"
                    :selected="index === activeIndex"
                    @activate="emit('activate', option)"
                    @remove="emit('remove-recent', option.title)"
                />
            </template>

            <template v-else>
                <SearchResultRow
                    v-for="(row, index) in resultRows"
                    :id="optionId(index)"
                    :key="`${row.kind}-${row.id}`"
                    :row="row"
                    :query="query"
                    :selected="index === activeIndex"
                    @activate="emit('activate', row)"
                />
                <!-- Presentational: a separator is not an allowed listbox child, so it is kept
                     out of the a11y tree rather than owned as one. -->
                <div
                    v-if="showSeeAll"
                    class="divider"
                    role="presentation"
                />
                <div
                    v-if="showSeeAll"
                    :id="optionId(seeAllIndex)"
                    :class="['see-all', { 'see-all-selected': isSeeAllSelected }]"
                    role="option"
                    :aria-selected="isSeeAllSelected ? 'true' : 'false'"
                    @click="emit('activate', { kind: 'see-all' })"
                >
                    <span class="see-all-label">See all results for “{{ query.trim() }}”</span>
                    <kbd
                        class="key"
                        aria-hidden="true"
                    >↵</kbd>
                </div>
            </template>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@use '@design' as *;

.search-typeahead {
    position: absolute;
    top: calc(100% + #{$space-8});
    right: 0;
    left: 0;
    z-index: $layer-message-z-index;
    padding: $space-8;
    border-radius: $radius-12;

    @include elevationOverlay;
}

.hint-row,
.section-heading,
.empty-message {
    @include secondary;

    display: flex;
    gap: $space-16;
    padding: $space-4 $space-8 $space-8;
    color: $c-text-tertiary;
}

.key {
    @include secondary;

    padding: 0 $space-4;
    margin-right: $space-4;
    font-family: inherit;
    color: $c-text-tertiary;
    border: 1px solid $c-border-default;
    border-radius: $radius-3;
}

.divider {
    height: 1px;
    margin: $space-8 0;
    background: $c-border-default;
}

.see-all {
    @include bodySmallEmphasis;

    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 40px;
    padding: 0 $space-8;
    cursor: pointer;
    border-radius: $radius-8;

    @include transition(background-color, fast);

    &.see-all-selected {
        background: $c-state-hover;
    }

    // Tablets render the panel — keep the highlight off after a tap.
    @media (hover: hover) {
        &:hover {
            background: $c-state-hover;
        }
    }
}
</style>
